import { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import {
  useCreateTask,
  useUsers,
  useAccounts,
  useContacts,
  useLeads,
  useAddTaskRelation,
} from '@/hooks/use-api'
import { usePicklistOptions } from '@/hooks/use-picklists'
import { useUser } from '@/contexts/UserContext'
import { X } from 'lucide-react'
import type { Task } from '@/types'
import { explainError } from '@/lib/explain-error'

interface CreateTaskModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  relatedToId?: string // Pre-fill who_id or what_id based on type
  relatedToType?: string // Entity type: "Lead", "Contact", "Account", "Opportunity"
  relatedToName?: string // Display name (e.g., "John Smith" or "Acme Corp")
  ownerId?: string // Pre-fill owner from parent entity's owner
  defaultSubject?: string // Pre-fill subject (e.g., follow-up tasks)
  defaultContacts?: SelectedContact[] // Pre-fill contacts (e.g., carry over from completed task)
}

interface SelectedContact {
  id: string
  name: string
  type: 'Contact' | 'Lead'
}

// Display label for a contact/lead search result. Leads are often company-only
// (no person name), which used to render as a blank, unclickable row — fall back
// to company, then email, so every result is identifiable and selectable.
const contactLabel = (c: { first_name?: string; last_name?: string; email?: string }) =>
  `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || 'Unnamed contact'
const leadLabel = (l: {
  first_name?: string
  last_name?: string
  company?: string
  email?: string
}) => `${l.first_name || ''} ${l.last_name || ''}`.trim() || l.company || l.email || 'Unnamed lead'

export function CreateTaskModal({
  open,
  onOpenChange,
  relatedToId,
  relatedToType,
  relatedToName,
  ownerId,
  defaultSubject,
  defaultContacts,
}: CreateTaskModalProps) {
  const { toast } = useToast()
  const { currentUser } = useUser()
  const createMutation = useCreateTask()
  const addRelationMutation = useAddTaskRelation()
  const { data: usersData } = useUsers({ limit: 10000 })
  const taskStatuses = usePicklistOptions('task', 'status')
  const taskPriorities = usePicklistOptions('task', 'priority')
  const taskTypes = usePicklistOptions('task', 'type')
  const callTypes = usePicklistOptions('task', 'call_type')
  const callDispositions = usePicklistOptions('task', 'call_disposition')

  const [formData, setFormData] = useState<Partial<Task & { who_id?: string; what_id?: string }>>({
    subject: '',
    status: 'Not Started',
    priority: 'Normal',
    owner_id: '',
    activity_date: '',
    description: '',
    who_id: '',
    what_id: '',
    type: 'Task',
    call_type: '',
    call_disposition: '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  // Related To (Account/Opportunity) state
  const [relatedSearch, setRelatedSearch] = useState('')
  const [selectedRelated, setSelectedRelated] = useState<{
    id: string
    name: string
    type: string
  } | null>(null)

  // Contacts / Leads multi-select state
  const [selectedContacts, setSelectedContacts] = useState<SelectedContact[]>([])
  const [contactSearch, setContactSearch] = useState('')
  const contactSearchRef = useRef<HTMLInputElement>(null)

  // Fetch entities for pickers
  // When an account is selected as "Related To", filter contacts to that account
  const relatedAccountId = selectedRelated?.type === 'Account' ? selectedRelated.id : undefined
  const { data: accountsData } = useAccounts({ search: relatedSearch, limit: 5 })
  const { data: contactsSearchData } = useContacts({
    search: contactSearch,
    limit: 5,
    account_id: relatedAccountId,
    status__c: 'Active',
  })
  const { data: leadsSearchData } = useLeads({ search: contactSearch, limit: 5 })

  const users = usersData?.data || []

  // Initialize form when modal opens
  useEffect(() => {
    if (open) {
      const updates: Partial<typeof formData> = {}
      updates.owner_id = ownerId || currentUser?.id || ''
      if (defaultSubject) {
        updates.subject = defaultSubject
      }

      // Pre-fill based on entity type
      if (relatedToId && relatedToType && relatedToName) {
        if (relatedToType === 'Contact' || relatedToType === 'Lead') {
          // Pre-fill as primary contact
          setSelectedContacts([
            { id: relatedToId, name: relatedToName, type: relatedToType as 'Contact' | 'Lead' },
          ])
          updates.who_id = relatedToId
          updates.what_id = ''
          setSelectedRelated(null)
        } else if (
          relatedToType === 'Account' ||
          relatedToType === 'Opportunity' ||
          relatedToType === 'Ticket'
        ) {
          // Pre-fill as Related To
          setSelectedRelated({ id: relatedToId, name: relatedToName, type: relatedToType })
          updates.what_id = relatedToId
          updates.who_id = ''
          setSelectedContacts([])
        }
      } else {
        setSelectedRelated(null)
        setSelectedContacts([])
      }

      // Carry over contacts from completed task (overrides relatedTo contact pre-fill)
      if (defaultContacts && defaultContacts.length > 0) {
        setSelectedContacts(defaultContacts)
        updates.who_id = defaultContacts[0]!.id
      }

      setFormData((prev) => ({ ...prev, ...updates }))
      setRelatedSearch('')
      setContactSearch('')
    }
  }, [open, relatedToId, relatedToType, relatedToName, ownerId, currentUser?.id, defaultSubject])

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}
    if (!formData.subject?.trim()) newErrors.subject = 'Subject is required'
    if (!formData.activity_date) newErrors.activity_date = 'Due date is required'
    if (!formData.owner_id) newErrors.owner_id = 'Owner is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields correctly.',
        variant: 'destructive',
      })
      return
    }

    try {
      // Create task with primary contact as who_id
      const taskPayload = { ...formData }
      if (selectedContacts.length > 0 && selectedContacts[0]) {
        taskPayload.who_id = selectedContacts[0].id
      }
      const createdTask = await createMutation.mutateAsync(taskPayload)

      // Add additional contacts as task_relations (skip first — already who_id)
      if (selectedContacts.length > 1 && createdTask.id) {
        const additionalContacts = selectedContacts.slice(1)
        await Promise.all(
          additionalContacts.map((contact) =>
            addRelationMutation.mutateAsync({ taskId: createdTask.id, relationId: contact.id })
          )
        )
      }

      toast({ title: 'Success', description: 'Task created successfully.' })

      // Reset form
      setFormData({
        subject: '',
        status: 'Not Started',
        priority: 'Normal',
        owner_id: currentUser?.id || '',
        activity_date: '',
        description: '',
        who_id: '',
        what_id: '',
        type: 'Task',
        call_type: '',
        call_disposition: '',
      })
      setErrors({})
      setSelectedRelated(null)
      setSelectedContacts([])
      setRelatedSearch('')
      setContactSearch('')
      onOpenChange(false)
    } catch (error) {
      const errorMessage =
        (error as any)?.response?.data?.error || explainError(error, 'Failed to create task')
      toast({ title: 'Error', description: errorMessage, variant: 'destructive' })
    }
  }

  const handleChange = (field: keyof typeof formData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const n = { ...prev }
        delete n[field]
        return n
      })
    }
  }

  // Add a contact/lead to the multi-select
  const addContact = (contact: SelectedContact) => {
    setSelectedContacts((prev) => [...prev, contact])
    // If first contact, set as who_id
    if (selectedContacts.length === 0) {
      handleChange('who_id', contact.id)
    }
    setContactSearch('')
    contactSearchRef.current?.focus()
  }

  // Remove a contact from the multi-select
  const removeContact = (contactId: string) => {
    setSelectedContacts((prev) => {
      const updated = prev.filter((c) => c.id !== contactId)
      // Update who_id: promote next contact or clear
      if (updated.length > 0 && updated[0]) {
        handleChange('who_id', updated[0].id)
      } else {
        handleChange('who_id', '')
      }
      return updated
    })
    contactSearchRef.current?.focus()
  }

  // IDs already selected (to filter from search results)
  const selectedContactIds = new Set(selectedContacts.map((c) => c.id))

  // Filter search results to exclude already-selected contacts
  const contactResults = (contactsSearchData?.data ?? [])
    .filter((c: any) => !selectedContactIds.has(c.id))
    .slice(0, 3)
  const leadResults = (leadsSearchData?.data ?? [])
    .filter((l: any) => !selectedContactIds.has(l.id))
    .slice(0, 3)

  const isPending = createMutation.isPending || addRelationMutation.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        aria-describedby="create-task-description"
      >
        <DialogHeader>
          <DialogTitle>Create New Task</DialogTitle>
          <DialogDescription id="create-task-description">
            Fill in the details to create a new task. All fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Subject */}
          <div>
            <Label htmlFor="subject" className="required">
              Subject *
            </Label>
            <Input
              id="subject"
              value={formData.subject}
              onChange={(e) => handleChange('subject', e.target.value)}
              placeholder="Enter task subject"
              aria-required="true"
              aria-invalid={!!errors.subject}
              aria-describedby={errors.subject ? 'subject-error' : undefined}
            />
            {errors.subject && (
              <p id="subject-error" className="text-sm text-destructive mt-1" role="alert">
                {errors.subject}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Status */}
            <div>
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {taskStatuses.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            {/* Priority */}
            <div>
              <Label htmlFor="priority">Priority</Label>
              <select
                id="priority"
                value={formData.priority}
                onChange={(e) => handleChange('priority', e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {taskPriorities.map((priority) => (
                  <option key={priority} value={priority}>
                    {priority}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Type */}
          <div>
            <Label htmlFor="type">Type</Label>
            <select
              id="type"
              value={formData.type || 'Task'}
              onChange={(e) => {
                handleChange('type', e.target.value)
                if (e.target.value !== 'Call') {
                  handleChange('call_type', '')
                  handleChange('call_disposition', '')
                }
              }}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {(taskTypes.length > 0
                ? taskTypes
                : ['Task', 'Call', 'Email', 'Meeting', 'Other']
              ).map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          {/* Call-specific fields */}
          {formData.type === 'Call' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="call_type">Direction</Label>
                <select
                  id="call_type"
                  value={formData.call_type || ''}
                  onChange={(e) => handleChange('call_type', e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">Select direction...</option>
                  {(callTypes.length > 0 ? callTypes : ['Inbound', 'Outbound']).map((ct) => (
                    <option key={ct} value={ct}>
                      {ct}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="call_disposition">Disposition</Label>
                <select
                  id="call_disposition"
                  value={formData.call_disposition || ''}
                  onChange={(e) => handleChange('call_disposition', e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">Select disposition...</option>
                  {(callDispositions.length > 0
                    ? callDispositions
                    : ['Connected', 'No Answer', 'Left Voicemail', 'Busy', 'Wrong Number']
                  ).map((cd) => (
                    <option key={cd} value={cd}>
                      {cd}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Owner */}
          <div>
            <Label htmlFor="owner_id" className="required">
              Owner *
            </Label>
            <select
              id="owner_id"
              value={formData.owner_id}
              onChange={(e) => handleChange('owner_id', e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-required="true"
              aria-invalid={!!errors.owner_id}
              aria-describedby={errors.owner_id ? 'owner-error' : undefined}
            >
              <option value="">Select an owner...</option>
              {[...users]
                .filter((u) => ['User', 'Admin', 'Super Admin'].includes(u.user_type))
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                .map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
            </select>
            {errors.owner_id && (
              <p id="owner-error" className="text-sm text-destructive mt-1" role="alert">
                {errors.owner_id}
              </p>
            )}
          </div>

          {/* Related To (Account/Opportunity only) */}
          {!(
            relatedToId &&
            (relatedToType === 'Account' ||
              relatedToType === 'Opportunity' ||
              relatedToType === 'Ticket')
          ) && (
            <div>
              <Label>Related To</Label>
              {selectedRelated ? (
                <div className="flex items-center gap-2 mt-1 p-2 border rounded-md bg-muted/50">
                  <span className="text-xs font-medium text-muted-foreground">
                    {selectedRelated.type}
                  </span>
                  <span className="font-medium text-sm">{selectedRelated.name}</span>
                  <button
                    type="button"
                    onClick={() => {
                      // Removing the related account/opp leaves the contacts intact —
                      // they're managed independently below.
                      setSelectedRelated(null)
                      handleChange('what_id', '')
                    }}
                    className="ml-auto text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                    aria-label={`Remove ${selectedRelated.name}`}
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <Input
                    placeholder="Search accounts..."
                    value={relatedSearch}
                    onChange={(e) => setRelatedSearch(e.target.value)}
                    aria-label="Search for related account or opportunity"
                    className="mt-1"
                  />
                  {relatedSearch.length >= 2 && (
                    <div
                      className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto"
                      role="listbox"
                      aria-label="Account search results"
                    >
                      {(accountsData?.data ?? []).slice(0, 5).map((a: any) => (
                        <button
                          key={a.id}
                          type="button"
                          role="option"
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
                          onClick={() => {
                            // Linking an account must NOT wipe already-selected contacts —
                            // a follow-up task often carries the contact over from the
                            // completed task and the user is just adding the account.
                            setSelectedRelated({ id: a.id, name: a.name, type: 'Account' })
                            handleChange('what_id', a.id)
                            setRelatedSearch('')
                          }}
                        >
                          <span className="text-xs text-muted-foreground mr-2">Account</span>
                          {a.name}
                        </button>
                      ))}
                      {relatedSearch.length >= 2 && !accountsData?.data?.length && (
                        <div className="px-3 py-2 text-sm text-muted-foreground">
                          No accounts found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {relatedToId && relatedToType === 'Account' && (
                <div className="flex items-center gap-2 mt-1 p-2 border rounded-md bg-muted/50">
                  <span className="text-xs font-medium text-muted-foreground">Account</span>
                  <span className="font-medium text-sm">{relatedToName}</span>
                </div>
              )}
            </div>
          )}

          {/* Pre-filled Related To indicator (when opened from Account/Opportunity/Ticket) */}
          {relatedToId &&
            (relatedToType === 'Account' ||
              relatedToType === 'Opportunity' ||
              relatedToType === 'Ticket') && (
              <div>
                <Label>Related To</Label>
                <div className="flex items-center gap-2 mt-1 p-2 border rounded-md bg-muted/50">
                  <span className="text-xs font-medium text-muted-foreground">{relatedToType}</span>
                  <span className="font-medium text-sm">{relatedToName}</span>
                </div>
              </div>
            )}

          {/* Contacts / Leads (multi-select) */}
          <div>
            <Label>Contacts / Leads</Label>
            {/* Selected contacts as chips */}
            {selectedContacts.length > 0 && (
              <div
                className="flex flex-wrap gap-2 mt-1 mb-2"
                role="list"
                aria-label="Selected contacts"
              >
                {selectedContacts.map((contact, index) => (
                  <span
                    key={contact.id}
                    role="listitem"
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border bg-muted/50 text-sm"
                  >
                    <span className="text-xs text-muted-foreground">{contact.type}</span>
                    <span className="font-medium">{contact.name}</span>
                    {index === 0 && (
                      <span className="text-xs text-muted-foreground">(primary)</span>
                    )}
                    <button
                      type="button"
                      onClick={() => removeContact(contact.id)}
                      className="ml-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                      aria-label={`Remove ${contact.name}`}
                    >
                      <X className="h-3 w-3" aria-hidden="true" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {/* Search input for adding contacts */}
            {!(
              relatedToId &&
              (relatedToType === 'Contact' || relatedToType === 'Lead') &&
              selectedContacts.length === 1 &&
              selectedContacts[0]?.id === relatedToId
            ) && (
              <div className="relative">
                <Input
                  ref={contactSearchRef}
                  placeholder={
                    selectedContacts.length > 0
                      ? 'Add another contact or lead...'
                      : 'Search contacts or leads...'
                  }
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  aria-label="Search for contacts or leads to add"
                  className="mt-1"
                />
                {contactSearch.length >= 2 && (
                  <div
                    className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto"
                    role="listbox"
                    aria-label="Contact and lead search results"
                  >
                    {contactResults.map((c: any) => (
                      <button
                        key={c.id}
                        type="button"
                        role="option"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
                        onClick={() => {
                          addContact({
                            id: c.id,
                            name: contactLabel(c),
                            type: 'Contact',
                          })
                        }}
                      >
                        <span className="text-xs text-muted-foreground mr-2">Contact</span>
                        {contactLabel(c)}
                      </button>
                    ))}
                    {leadResults.map((l: any) => (
                      <button
                        key={l.id}
                        type="button"
                        role="option"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
                        onClick={() => {
                          addContact({
                            id: l.id,
                            name: leadLabel(l),
                            type: 'Lead',
                          })
                        }}
                      >
                        <span className="text-xs text-muted-foreground mr-2">Lead</span>
                        {leadLabel(l)}
                      </button>
                    ))}
                    {contactSearch.length >= 2 &&
                      contactResults.length === 0 &&
                      leadResults.length === 0 && (
                        <div className="px-3 py-2 text-sm text-muted-foreground">
                          No contacts or leads found
                        </div>
                      )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Due Date */}
          <div>
            <Label htmlFor="activity_date" className="required">
              Due Date *
            </Label>
            <DatePicker
              id="activity_date"
              value={formData.activity_date}
              onChange={(v) => handleChange('activity_date', v ?? '')}
              aria-label="Task due date"
              aria-required="true"
              aria-invalid={!!errors.activity_date}
              aria-describedby={errors.activity_date ? 'activity_date-error' : undefined}
            />
            {errors.activity_date && (
              <p id="activity_date-error" className="text-sm text-destructive mt-1" role="alert">
                {errors.activity_date}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="description">Description</Label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="Enter task description..."
              aria-label="Task description"
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
              aria-label="Cancel and close modal"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isPending}
              aria-label={isPending ? 'Creating task...' : 'Create task'}
            >
              {isPending ? 'Creating...' : 'Create Task'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
